/**
 * useLoginCms — resolves the tenant's brand + theme for the sign-in page.
 *
 * Hydrates SYNCHRONOUSLY from the shared theme cache (populated by
 * `useLandingCMS` on any prior visit) so the initial paint is already
 * branded. Then fires the CMS request in the background to pick up any
 * newer edits. The cache write in useLandingCMS keeps this warm for
 * subsequent loads.
 */
import { useEffect, useState } from "react";
import api from "@/api/client";
import { CMS_CACHE_KEY } from "@/hooks/useLandingCMS";

const DEFAULT_CUSTOMER_THEME = {
  gradient_from: "#EA580C", gradient_to: "#F59E0B", accent: "#F97316",
  card_bg: "#FFFFFF", panel_bg: "#FFF9F5", text_on_gradient: "#FFFFFF",
};

const DEFAULT_STAFF_THEME = {
  bg: "#020617", card_bg: "#0F172A", accent: "#F97316", accent_secondary: "#38BDF8",
  text: "#F1F5F9", muted_text: "#94A3B8", orb_1: "#F97316", orb_2: "#0EA5E9",
};

const DEFAULT_BRAND = {
  name: "FUELEXPRESS",
  logo_url: "",
  customer_login_tagline: "Doorstep diesel delivery",
  staff_login_tagline: "Operations command centre",
  customer_login_enabled: true,
};

function readCache() {
  try { return JSON.parse(localStorage.getItem(CMS_CACHE_KEY) || "null") || {}; }
  catch { return {}; }
}

export function useLoginCms() {
  const cached = readCache();
  const [brand, setBrand] = useState({ ...DEFAULT_BRAND, ...(cached.brand || {}) });
  const [customerTheme, setCustomerTheme] = useState({ ...DEFAULT_CUSTOMER_THEME, ...(cached.customer_theme || {}) });
  const [staffTheme, setStaffTheme] = useState({ ...DEFAULT_STAFF_THEME, ...(cached.staff_theme || {}) });

  useEffect(() => {
    api.get("/cms/landing").then((r) => {
      const c = r.data?.content || r.data || {};
      setBrand({
        name: c?.brand?.name || DEFAULT_BRAND.name,
        logo_url: c?.brand?.logo_url || "",
        customer_login_tagline: c?.brand?.customer_login_tagline || DEFAULT_BRAND.customer_login_tagline,
        staff_login_tagline: c?.brand?.staff_login_tagline || DEFAULT_BRAND.staff_login_tagline,
        customer_login_enabled: c?.brand?.customer_login_enabled !== false,
      });
      if (c?.customer_theme) setCustomerTheme((t) => ({ ...t, ...c.customer_theme }));
      if (c?.staff_theme) setStaffTheme((t) => ({ ...t, ...c.staff_theme }));
    }).catch(() => { /* fall through to defaults */ });
  }, []);

  return { brand, customerTheme, staffTheme };
}
